/* Copyright (c) 2026 Dobler Data Solutions, LLC. All rights reserved. */

import { useEffect, useMemo, useState } from 'react';
import type { TaxonomyWithCountDto, TaxonomyFormulaAssignmentDto } from '@shared/types';
import { fetchFormulasForTaxonomy, removeAssignment } from '../../services/formularyApi';

interface Props {
  taxonomy: TaxonomyWithCountDto;
  onClose: () => void;
  /** Called after a successful removal so the parent can refresh counts. */
  onAssignmentRemoved?: () => void;
}

/**
 * Modal listing all formulas currently assigned to the given taxonomy code,
 * sorted by assignment date DESC (most recent first). Each row has a Remove
 * button that soft-deletes the assignment via DELETE /api/assignments/:xlinkSetId.
 */
export function TaxonomyFormulasModal({ taxonomy, onClose, onAssignmentRemoved }: Props) {
  const [rows, setRows] = useState<TaxonomyFormulaAssignmentDto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pendingRemoveId, setPendingRemoveId] = useState<string | null>(null);
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');

  const filteredRows = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => r.formulaName.toLowerCase().includes(q));
  }, [rows, searchText]);

  /** null = server order (assigned date DESC). Empty sources sort last. */
  const [sourceSort, setSourceSort] = useState<'asc' | 'desc' | null>(null);

  const sortedRows = useMemo(() => {
    if (!sourceSort) return filteredRows;
    const dir = sourceSort === 'asc' ? 1 : -1;
    return [...filteredRows].sort((a, b) => {
      const sa = a.formulaSourceName || '';
      const sb = b.formulaSourceName || '';
      if (!sa !== !sb) return sa ? -1 : 1;
      return dir * sa.localeCompare(sb) || a.formulaName.localeCompare(b.formulaName);
    });
  }, [filteredRows, sourceSort]);

  function handleSourceSortToggle() {
    setSourceSort((prev) => (prev === 'asc' ? 'desc' : prev === 'desc' ? null : 'asc'));
  }

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);
    fetchFormulasForTaxonomy(taxonomy.setId)
      .then((res) => { if (!cancelled) setRows(res.data); })
      .catch((err) => { if (!cancelled) setError(err?.message || 'Failed to load formulas'); })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [taxonomy.setId]);

  // Close on Esc
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  async function handleRemove(row: TaxonomyFormulaAssignmentDto) {
    setRemovingId(row.xlinkSetId);
    setError(null);
    try {
      await removeAssignment(row.xlinkSetId, 'Removed from Taxonomy view');
      setRows((prev) => prev.filter((r) => r.xlinkSetId !== row.xlinkSetId));
      onAssignmentRemoved?.();
    } catch (err) {
      setError((err as Error).message || 'Failed to remove assignment');
    } finally {
      setRemovingId(null);
      setPendingRemoveId(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" onClick={onClose}>
      <div
        className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-start justify-between px-5 py-4 border-b border-gray-200">
          <div className="min-w-0">
            <div className="text-xs uppercase tracking-wide text-gray-500">Assigned formulas</div>
            <h3 className="text-lg font-semibold text-gray-900 truncate">
              <span className="text-dds-blue-500 font-mono mr-2">{taxonomy.code}</span>
              <span>{taxonomy.displayName}</span>
            </h3>
            <div className="text-sm text-gray-500 mt-0.5">
              {isLoading
                ? 'Loading…'
                : searchText.trim()
                  ? `${filteredRows.length} of ${rows.length} assignment${rows.length === 1 ? '' : 's'}`
                  : `${rows.length} assignment${rows.length === 1 ? '' : 's'}`}
            </div>
          </div>
          <button
            onClick={onClose}
            className="ml-4 text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="Close"
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-auto">
          {!isLoading && rows.length > 0 && (
            <div className="px-5 py-3 border-b border-gray-100 bg-gray-50/50 sticky top-0 z-10">
              <div className="relative w-80">
                <input
                  type="text"
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  placeholder="Search formula name…"
                  className="w-full text-sm border border-gray-200 rounded px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-dds-blue-500 focus:border-transparent"
                />
                {searchText && (
                  <button
                    onClick={() => setSearchText('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-sm"
                    aria-label="Clear search"
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>
          )}

          {error && (
            <div className="m-4 bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="text-center text-gray-400 py-12">Loading formulas…</div>
          ) : rows.length === 0 ? (
            <div className="text-center text-gray-400 py-12">No active assignments.</div>
          ) : filteredRows.length === 0 ? (
            <div className="text-center text-gray-400 py-12">No formulas match &quot;{searchText}&quot;.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-gray-50 sticky top-0">
                <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b border-gray-200">
                  <th className="px-4 py-2.5 w-36">Action</th>
                  <th className="px-4 py-2.5">Formula</th>
                  <th className="px-4 py-2.5">Dosage Form</th>
                  <th className="px-4 py-2.5">Strength</th>
                  <th className="px-4 py-2.5">Schedule</th>
                  <th
                    className="px-4 py-2.5"
                    aria-sort={sourceSort === 'asc' ? 'ascending' : sourceSort === 'desc' ? 'descending' : 'none'}
                  >
                    <button
                      type="button"
                      onClick={handleSourceSortToggle}
                      className="inline-flex items-center gap-1 uppercase tracking-wider hover:text-gray-700"
                    >
                      Source
                      {sourceSort === 'asc' && <span aria-hidden="true">▲</span>}
                      {sourceSort === 'desc' && <span aria-hidden="true">▼</span>}
                    </button>
                  </th>
                  <th className="px-4 py-2.5">Assigned</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sortedRows.map((r) => {
                  const isConfirming = pendingRemoveId === r.xlinkSetId;
                  const isRemoving = removingId === r.xlinkSetId;
                  return (
                    <tr key={r.xlinkSetId} className="hover:bg-gray-50">
                      <td className="px-4 py-2">
                        {isConfirming ? (
                          <div className="inline-flex items-center gap-1">
                            <button
                              onClick={() => handleRemove(r)}
                              disabled={isRemoving}
                              className="px-2 py-1 text-xs rounded bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
                            >
                              {isRemoving ? 'Removing…' : 'Confirm'}
                            </button>
                            <button
                              onClick={() => setPendingRemoveId(null)}
                              disabled={isRemoving}
                              className="px-2 py-1 text-xs rounded border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-60"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setPendingRemoveId(r.xlinkSetId)}
                            className="px-2 py-1 text-xs rounded border border-red-200 text-red-600 hover:bg-red-50"
                          >
                            Remove
                          </button>
                        )}
                      </td>
                      <td className="px-4 py-2 font-medium text-gray-900">{r.formulaName}</td>
                      <td className="px-4 py-2 text-gray-600">{r.dosageForm || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">{r.strength || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">{r.schedule || '—'}</td>
                      <td className="px-4 py-2 text-gray-600">{r.formulaSourceName || '—'}</td>
                      <td className="px-4 py-2 text-gray-600 whitespace-nowrap">
                        {new Date(r.assignedAt).toLocaleDateString()}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end px-5 py-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm rounded border border-gray-300 text-gray-700 hover:bg-white"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}
